import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { equipeService, CreateEquipeDTO, AgentWithEquipe } from '@/services/equipeService';
import { materielService } from '@/services/materielService';
import type { EquipeDeneignement } from '@/types';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { ConfirmModal } from '@/components/common/Modal';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  UserMinusIcon,
  MagnifyingGlassIcon,
  ArrowsRightLeftIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TruckIcon,
  XMarkIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

/** Formate une date ISO (YYYY-MM-DD, telle que renvoyee par l'API) en jj/mm/aaaa */
function formatDateFr(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Date du jour (calendrier LOCAL de l'utilisateur, pas UTC) au format
 * YYYY-MM-DD, meme format que l'API. `.toISOString()` convertirait en UTC
 * et donnerait la veille entre minuit et l'heure du decalage local (ex.
 * jusqu'a 2h du matin heure de Paris, UTC+2) - on reste donc en local.
 */
function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Page de gestion des equipes de deneigement
 */
export function EquipesPage() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingEquipe, setEditingEquipe] = useState<EquipeDeneignement | null>(null);
  const [showAgentModal, setShowAgentModal] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EquipeDeneignement | null>(null);
  const [removeAgentTarget, setRemoveAgentTarget] = useState<{ equipeId: number; agent: { id: number; prenom: string; nom: string } } | null>(null);

  // Jour sur lequel se base la repartition (par defaut aujourd'hui) -
  // demande utilisateur : "pouvoir faire des anticipations de planification
  // sur la journee courante plus ou moins" - navigable jour par jour.
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const navigateDate = (direction: number) => {
    // Construction/lecture en UTC de bout en bout : un `new Date(...)` suivi
    // de `.toISOString()` repasse par l'heure locale puis l'UTC, ce qui peut
    // faire "sauter" la date d'un jour selon le fuseau horaire du navigateur
    // (meme classe de bug que `combineDateEtHeure()` cote backend).
    const [y, m, d] = selectedDate.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + direction);
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    setSelectedDate(`${yyyy}-${mm}-${dd}`);
  };

  // Chargement des equipes
  const { data: equipes, isLoading } = useQuery({
    queryKey: ['equipes', selectedDate],
    queryFn: () => equipeService.list(selectedDate),
  });

  // Tous les agents de deneignement, avec leur statut de planning a `date`
  const { data: agentsData } = useQuery({
    queryKey: ['all-agents', selectedDate],
    queryFn: () => equipeService.getAllAgents(selectedDate),
  });
  const allAgents = agentsData?.agents;

  // Tous les chefs d'equipe (candidats pour le champ chef_id), meme logique
  const { data: chefsData } = useQuery({
    queryKey: ['all-chefs', selectedDate],
    queryFn: () => equipeService.getAllChefs(selectedDate),
  });
  const allChefs = chefsData?.chefs;
  // Les deux endpoints evaluent "au planning" pour la meme date - n'importe
  // laquelle des deux reponses convient pour l'affichage, `selectedDate` en
  // secours tant que les requetes n'ont pas encore repondu.
  const datePlanning = chefsData?.date ?? agentsData?.date ?? selectedDate;

  // Vehicules (referentiel materiel) candidats pour le champ engin_id -
  // demande utilisateur : "pouvoir affecter a l'equipe un engin pour que le
  // processus soit totalement operationnel". Tous les vehicules actifs sont
  // listes (pas seulement les disponibles) : meme logique que chef/agent,
  // les indisponibles sont marques plutot que caches, pour ne pas
  // deselectionner silencieusement le vehicule actuel d'une equipe en cours
  // d'edition s'il n'est plus disponible au moment de l'ouvrir.
  const { data: materiels } = useQuery({
    queryKey: ['materiels-actifs'],
    queryFn: () => materielService.list({ actif: true }),
  });

  // Filtre de recherche pour le modal
  const [agentSearch, setAgentSearch] = useState('');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);

  // Affectation du vehicule directement depuis la carte equipe (meme UX que
  // les agents) - demande utilisateur : "pouvoir la modifier directement
  // depuis le bloc equipe comme on le fait pour les agents", plutot que de
  // devoir ouvrir la modale generale "Modifier l'equipe".
  const [showEnginModal, setShowEnginModal] = useState<number | null>(null);
  const [enginSearch, setEnginSearch] = useState('');
  const [removeEnginTarget, setRemoveEnginTarget] = useState<{ equipeId: number; engin: { id: number; nom: string } } | null>(null);

  // Affectation du chef directement depuis la carte equipe, meme UX que
  // vehicule/agents - demande utilisateur : "le même principe que les
  // agents avec un modal 'affecter un Chef d'équipe à une équipe'", plutot
  // que le select dans la modale generale "Modifier l'equipe".
  const [showChefModal, setShowChefModal] = useState<number | null>(null);
  const [chefSearch, setChefSearch] = useState('');
  const [removeChefTarget, setRemoveChefTarget] = useState<{ equipeId: number; chef: { id: number; prenom: string; nom: string } } | null>(null);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateEquipeDTO) => equipeService.create(data, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      setShowModal(false);
      toast.success('Equipe creee avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateEquipeDTO> }) =>
      equipeService.update(id, data, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      setShowModal(false);
      setEditingEquipe(null);
      toast.success('Equipe modifiee avec succes');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => equipeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      toast.success('Equipe supprimee');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const addAgentMutation = useMutation({
    mutationFn: ({ equipeId, agentId }: { equipeId: number; agentId: number }) =>
      equipeService.addAgent(equipeId, agentId, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      queryClient.invalidateQueries({ queryKey: ['all-agents'] });
      toast.success('Agent affecte a l\'equipe');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors de l\'affectation';
      toast.error(message);
    },
  });

  const removeAgentMutation = useMutation({
    mutationFn: ({ equipeId, agentId }: { equipeId: number; agentId: number }) =>
      equipeService.removeAgent(equipeId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      queryClient.invalidateQueries({ queryKey: ['all-agents'] });
      toast.success('Agent retire de l\'equipe');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Erreur lors du retrait';
      toast.error(message);
    },
  });

  // Affectation/retrait direct du vehicule (reutilise PUT /equipes/{id} -
  // l'engin est un champ simple, pas une collection comme les agents, donc
  // pas besoin d'endpoints dedies). Mutation distincte de `updateMutation`
  // (formulaire general "Modifier l'equipe") pour ne pas coupler les deux
  // flux : celle-ci ne doit pas fermer ce formulaire-la.
  const updateEnginMutation = useMutation({
    mutationFn: ({ equipeId, enginId }: { equipeId: number; enginId: number | null }) =>
      equipeService.update(equipeId, { enginId }, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      toast.success('Vehicule mis a jour');
      setShowEnginModal(null);
      setEnginSearch('');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  // Affectation/retrait direct du chef, meme logique que le vehicule
  // ci-dessus (champ simple sur l'equipe, pas une collection) - mutation
  // distincte de `updateMutation` pour ne pas coupler les deux flux.
  const updateChefMutation = useMutation({
    mutationFn: ({ equipeId, chefId }: { equipeId: number; chefId: number | null }) =>
      equipeService.update(equipeId, { chefId }, selectedDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipes'] });
      queryClient.invalidateQueries({ queryKey: ['all-chefs'] });
      toast.success("Chef d'equipe mis a jour");
      setShowChefModal(null);
      setChefSearch('');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: CreateEquipeDTO = {
      nom: formData.get('nom') as string,
      code: formData.get('code') as string,
    };

    if (editingEquipe) {
      updateMutation.mutate({ id: editingEquipe.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (equipe: EquipeDeneignement) => {
    setDeleteTarget(equipe);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const handleRemoveAgent = (equipeId: number, agent: { id: number; prenom: string; nom: string }) => {
    setRemoveAgentTarget({ equipeId, agent });
  };

  const confirmRemoveAgent = () => {
    if (removeAgentTarget) {
      removeAgentMutation.mutate({
        equipeId: removeAgentTarget.equipeId,
        agentId: removeAgentTarget.agent.id,
      });
      setRemoveAgentTarget(null);
    }
  };

  const handleRemoveEngin = (equipeId: number, engin: { id: number; nom: string }) => {
    setRemoveEnginTarget({ equipeId, engin });
  };

  const confirmRemoveEngin = () => {
    if (removeEnginTarget) {
      updateEnginMutation.mutate({ equipeId: removeEnginTarget.equipeId, enginId: null });
      setRemoveEnginTarget(null);
    }
  };

  const handleRemoveChef = (equipeId: number, chef: { id: number; prenom: string; nom: string }) => {
    setRemoveChefTarget({ equipeId, chef });
  };

  const confirmRemoveChef = () => {
    if (removeChefTarget) {
      updateChefMutation.mutate({ equipeId: removeChefTarget.equipeId, chefId: null });
      setRemoveChefTarget(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des equipes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-gray-900">Gestion des equipes</h2>
            <div className="inline-flex items-center gap-1">
              <button
                onClick={() => navigateDate(-1)}
                className="p-1 rounded hover:bg-gray-200 text-gray-500"
                title="Jour precedent"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-sm font-semibold">
                <CalendarDaysIcon className="w-4 h-4" />
                Planning du {formatDateFr(datePlanning)}
              </span>
              <button
                onClick={() => navigateDate(1)}
                className="p-1 rounded hover:bg-gray-200 text-gray-500"
                title="Jour suivant"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
              {selectedDate !== todayIso() && (
                <button
                  onClick={() => setSelectedDate(todayIso())}
                  className="text-xs text-primary-600 hover:underline ml-1"
                >
                  Aujourd'hui
                </button>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Un chef ou un agent n'apparait comme affectable que s'il a une vacation couvrant cette date.
          </p>
        </div>
        <button
          onClick={() => {
            setEditingEquipe(null);
            setShowModal(true);
          }}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Nouvelle equipe
        </button>
      </div>

      {/* Liste des equipes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {equipes?.map((equipe) => (
          <div key={equipe.id} className="card">
            <div className="card-body">
              {/* Header equipe */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{equipe.nom}</h3>
                  <p className="text-sm text-gray-500">Code: {equipe.code}</p>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    equipe.actif
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {equipe.actif ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Chef d'equipe - affectable/modifiable directement depuis la
                  carte, meme UX que vehicule/agents, plutot que de passer
                  par la modale generale "Modifier l'equipe". L'identite
                  n'est affichee que si le chef est reellement au planning
                  (chefVacation non-null) : un chef assigne mais pas
                  planifie ne doit pas apparaitre comme s'il etait present
                  (signale par l'utilisateur). */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500 uppercase">Chef d'equipe</p>
                  <button
                    onClick={() => setShowChefModal(equipe.id)}
                    className="text-primary-500 hover:text-primary-600"
                    title={equipe.chef && equipe.chefVacation ? 'Changer de chef' : "Affecter un chef"}
                  >
                    <UserIcon className="w-4 h-4" />
                  </button>
                </div>
                {equipe.chef && equipe.chefVacation ? (
                  <div className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                    <div>
                      <p>{equipe.chef.prenom} {equipe.chef.nom}</p>
                      <p className="text-xs text-gray-500">
                        En poste le {formatDateFr(equipe.chefVacation.date)} de {equipe.chefVacation.heureDebut} a{' '}
                        {equipe.chefVacation.heureFin}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveChef(equipe.id, equipe.chef!)}
                      className="text-red-400 hover:text-red-600"
                      title="Retirer"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Aucun chef affecte</p>
                )}
              </div>

              {/* Vehicule - affectable/modifiable directement depuis la
                  carte, meme UX que les agents (plutot que d'obliger a
                  passer par la modale "Modifier l'equipe"). */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500 uppercase">Vehicule</p>
                  <button
                    onClick={() => setShowEnginModal(equipe.id)}
                    className="text-primary-500 hover:text-primary-600"
                    title={equipe.engin ? 'Changer de vehicule' : 'Affecter un vehicule'}
                  >
                    <TruckIcon className="w-4 h-4" />
                  </button>
                </div>
                {equipe.engin ? (
                  <div className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                    <span>
                      {equipe.engin.nom}
                      {equipe.engin.immatriculation ? ` (${equipe.engin.immatriculation})` : ''}
                      {!equipe.engin.disponible && (
                        <span className="text-amber-600"> - {equipe.engin.etat}</span>
                      )}
                    </span>
                    <button
                      onClick={() => handleRemoveEngin(equipe.id, equipe.engin!)}
                      className="text-red-400 hover:text-red-600"
                      title="Retirer"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Aucun vehicule affecte</p>
                )}
              </div>

              {/* Agents */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 uppercase">
                    Agents ({equipe.agents?.length || 0})
                  </p>
                  <button
                    onClick={() => setShowAgentModal(equipe.id)}
                    className="text-primary-500 hover:text-primary-600"
                    title="Ajouter un agent"
                  >
                    <UserPlusIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1">
                  {equipe.agents?.slice(0, 3).map((agent) => (
                    <div
                      key={agent.id}
                      className="flex items-center justify-between text-sm bg-gray-50 px-2 py-1 rounded"
                    >
                      <span>
                        {agent.prenom} {agent.nom}
                      </span>
                      <button
                        onClick={() => handleRemoveAgent(equipe.id, agent)}
                        className="text-red-400 hover:text-red-600"
                        title="Retirer"
                      >
                        <UserMinusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {equipe.agents?.length > 3 && (
                    <p className="text-xs text-gray-400">
                      +{equipe.agents.length - 3} autres
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-2 pt-4 border-t">
                <button
                  onClick={() => {
                    setEditingEquipe(equipe);
                    setShowModal(true);
                  }}
                  className="p-2 text-gray-400 hover:text-primary-500"
                  title="Modifier"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(equipe)}
                  className="p-2 text-gray-400 hover:text-red-500"
                  title="Supprimer"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {equipes?.length === 0 && (
        <div className="text-center py-12">
          <UserGroupIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-3">Aucune equipe configuree</p>
          <button
            onClick={() => { setEditingEquipe(null); setShowModal(true); }}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Creer une equipe
          </button>
        </div>
      )}

      {/* Modale confirmation suppression */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={`Supprimer l'equipe "${deleteTarget?.nom}" ?`}
        message="Cette action est irreversible. L'equipe et ses affectations seront supprimees."
        confirmLabel="Supprimer"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Modale confirmation retrait agent */}
      <ConfirmModal
        isOpen={!!removeAgentTarget}
        onClose={() => setRemoveAgentTarget(null)}
        onConfirm={confirmRemoveAgent}
        title="Retirer l'agent de l'equipe ?"
        message={removeAgentTarget
          ? `Retirer ${removeAgentTarget.agent.prenom} ${removeAgentTarget.agent.nom} de cette equipe ?`
          : ''}
        confirmLabel="Retirer"
        variant="warning"
        isLoading={removeAgentMutation.isPending}
      />

      {/* Modale confirmation retrait vehicule */}
      <ConfirmModal
        isOpen={!!removeEnginTarget}
        onClose={() => setRemoveEnginTarget(null)}
        onConfirm={confirmRemoveEngin}
        title="Retirer le vehicule de l'equipe ?"
        message={removeEnginTarget ? `Retirer ${removeEnginTarget.engin.nom} de cette equipe ?` : ''}
        confirmLabel="Retirer"
        variant="warning"
        isLoading={updateEnginMutation.isPending}
      />

      {/* Modale confirmation retrait chef */}
      <ConfirmModal
        isOpen={!!removeChefTarget}
        onClose={() => setRemoveChefTarget(null)}
        onConfirm={confirmRemoveChef}
        title="Retirer le chef de l'equipe ?"
        message={removeChefTarget ? `Retirer ${removeChefTarget.chef.prenom} ${removeChefTarget.chef.nom} de cette equipe ?` : ''}
        confirmLabel="Retirer"
        variant="warning"
        isLoading={updateChefMutation.isPending}
      />

      {/* Modal creation/edition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingEquipe ? 'Modifier l\'equipe' : 'Nouvelle equipe'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Nom</label>
                <input
                  type="text"
                  name="nom"
                  defaultValue={editingEquipe?.nom}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Code</label>
                <input
                  type="text"
                  name="code"
                  defaultValue={editingEquipe?.code}
                  className="input"
                  required
                  maxLength={10}
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEquipe(null);
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  {editingEquipe ? 'Modifier' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ajout agent */}
      {showAgentModal && (() => {
        const currentEquipe = equipes?.find(e => e.id === showAgentModal);

        // Base commune a la liste et au compteur en pied de modale : un
        // agent "eligible" est au planning et pas deja membre de CETTE
        // equipe (recherche/case a cocher n'affinent que l'affichage,
        // jamais ce compteur de reference - sinon il varie avec la saisie
        // et redevient aussi trompeur que l'ancien "X au total").
        const eligibleAgents = (allAgents || []).filter((agent) =>
          agent.auPlanningAujourdhui && !currentEquipe?.agents?.some(a => a.id === agent.id)
        );
        const nbLibres = eligibleAgents.filter((a) => !a.equipeActuelle).length;
        const nbATransferer = eligibleAgents.filter((a) => a.equipeActuelle).length;

        const filteredAgents = eligibleAgents.filter((agent) => {
          const searchMatch = agentSearch === '' ||
            agent.nomComplet.toLowerCase().includes(agentSearch.toLowerCase()) ||
            agent.email.toLowerCase().includes(agentSearch.toLowerCase());

          const availableMatch = !showOnlyAvailable || !agent.equipeActuelle;

          return searchMatch && availableMatch;
        });

        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold">
              Affecter un agent a l'equipe
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Seuls les agents au planning {datePlanning ? `du ${formatDateFr(datePlanning)}` : "aujourd'hui"} sont proposes ici.
            </p>

            {/* Filtres */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="Rechercher un agent..."
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                />
              </div>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={showOnlyAvailable}
                  onChange={(e) => setShowOnlyAvailable(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span>Afficher uniquement les agents sans equipe</span>
              </label>
            </div>

            {/* Liste des agents */}
            <div className="border rounded-lg max-h-64 overflow-y-auto mb-4">
              {(() => {
                if (filteredAgents.length === 0) {
                  return (
                    <p className="text-gray-500 text-center py-8">
                      Aucun agent trouve
                    </p>
                  );
                }

                return filteredAgents.map((agent) => (
                  <div
                    key={agent.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div>
                      <p className="font-medium">{agent.nomComplet}</p>
                      <p className="text-sm text-gray-500">{agent.email}</p>
                      {agent.equipeActuelle && (
                        <p className="text-xs text-orange-600">
                          Actuellement dans: {agent.equipeActuelle.nom}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        addAgentMutation.mutate({
                          equipeId: showAgentModal,
                          agentId: agent.id,
                        });
                      }}
                      disabled={addAgentMutation.isPending}
                      className={`flex items-center px-3 py-1.5 rounded text-sm ${
                        agent.equipeActuelle
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                      }`}
                      title={agent.equipeActuelle ? 'Transferer' : 'Affecter'}
                    >
                      {agent.equipeActuelle ? (
                        <>
                          <ArrowsRightLeftIcon className="w-4 h-4 mr-1" />
                          Transferer
                        </>
                      ) : (
                        <>
                          <UserPlusIcon className="w-4 h-4 mr-1" />
                          Affecter
                        </>
                      )}
                    </button>
                  </div>
                ));
              })()}
            </div>

            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {/* Compte uniquement les agents eligibles a CETTE equipe
                    (au planning, pas deja membre) - pas tous les agents du
                    referentiel comme l'ancien "X au total", qui ne
                    correspondait jamais au nombre reellement affichable. */}
                {nbLibres} libre(s)
                {' · '}
                {nbATransferer} a transferer
              </p>
              <button
                onClick={() => {
                  setShowAgentModal(null);
                  setAgentSearch('');
                  setShowOnlyAvailable(false);
                }}
                className="btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Modal affectation vehicule - meme structure que la modale agent,
          mais remplace l'affectation courante plutot que d'ajouter (un seul
          vehicule par equipe). */}
      {showEnginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold">Affecter un vehicule a l'equipe</h3>
            <p className="text-xs text-gray-500 mb-4">
              Seul un vehicule disponible peut etre affecte (⚠ ci-dessous sinon).
            </p>

            <div className="relative mb-4">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Rechercher un vehicule..."
                value={enginSearch}
                onChange={(e) => setEnginSearch(e.target.value)}
              />
            </div>

            <div className="border rounded-lg max-h-64 overflow-y-auto mb-4">
              {(() => {
                const currentEquipe = equipes?.find((e) => e.id === showEnginModal);
                const filteredMateriels = materiels?.filter((materiel) => {
                  const searchMatch =
                    enginSearch === '' ||
                    materiel.nom.toLowerCase().includes(enginSearch.toLowerCase()) ||
                    materiel.immatriculation?.toLowerCase().includes(enginSearch.toLowerCase());

                  // Exclut le vehicule deja affecte a cette equipe (rien a
                  // faire dessus ici, le retirer se fait directement sur
                  // la carte).
                  const notCurrentEngin = materiel.id !== currentEquipe?.engin?.id;

                  return searchMatch && notCurrentEngin;
                });

                if (!filteredMateriels || filteredMateriels.length === 0) {
                  return <p className="text-gray-500 text-center py-8">Aucun vehicule trouve</p>;
                }

                return filteredMateriels.map((materiel) => {
                  const equipeActuelle = equipes?.find((e) => e.engin?.id === materiel.id);

                  return (
                    <div
                      key={materiel.id}
                      className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div>
                        <p className="font-medium flex items-center gap-1">
                          {materiel.nom}
                          {!materiel.disponible && (
                            <span className="text-amber-600 text-xs font-normal" title={materiel.etat}>
                              ⚠ {materiel.etat}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">
                          {materiel.type}
                          {materiel.immatriculation ? ` - ${materiel.immatriculation}` : ''}
                        </p>
                        {equipeActuelle && (
                          <p className="text-xs text-orange-600">
                            Actuellement dans: {equipeActuelle.nom}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          updateEnginMutation.mutate({ equipeId: showEnginModal, enginId: materiel.id })
                        }
                        disabled={updateEnginMutation.isPending}
                        className={`flex items-center px-3 py-1.5 rounded text-sm ${
                          equipeActuelle
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                        }`}
                        title={equipeActuelle ? 'Transferer' : 'Affecter'}
                      >
                        {equipeActuelle ? (
                          <>
                            <ArrowsRightLeftIcon className="w-4 h-4 mr-1" />
                            Transferer
                          </>
                        ) : (
                          <>
                            <TruckIcon className="w-4 h-4 mr-1" />
                            Affecter
                          </>
                        )}
                      </button>
                    </div>
                  );
                });
              })()}
            </div>

            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">{materiels?.length || 0} vehicule(s) au total</p>
              <button
                onClick={() => {
                  setShowEnginModal(null);
                  setEnginSearch('');
                }}
                className="btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal affectation chef - meme principe que la modale agent (liste
          filtree par planning, compteur libre/a transferer), mais remplace
          l'affectation courante plutot que d'ajouter (un seul chef par
          equipe, meme logique que le vehicule). */}
      {showChefModal && (() => {
        const currentEquipe = equipes?.find((e) => e.id === showChefModal);

        // Meme principe que la modale agent : un chef "eligible" est au
        // planning et n'est pas deja le chef de CETTE equipe (le retirer se
        // fait directement sur la carte, pas ici).
        const eligibleChefs = (allChefs || []).filter((chef) =>
          chef.auPlanningAujourdhui && chef.id !== currentEquipe?.chef?.id
        );
        const nbLibres = eligibleChefs.filter((c) => !c.equipeActuelle).length;
        const nbATransferer = eligibleChefs.filter((c) => c.equipeActuelle).length;

        const filteredChefs = eligibleChefs.filter((chef) =>
          chefSearch === '' ||
          chef.nomComplet.toLowerCase().includes(chefSearch.toLowerCase()) ||
          chef.email.toLowerCase().includes(chefSearch.toLowerCase())
        );

        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold">
              Affecter un chef d'equipe a l'equipe
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Seuls les chefs au planning {datePlanning ? `du ${formatDateFr(datePlanning)}` : "aujourd'hui"} sont proposes ici.
            </p>

            <div className="relative mb-4">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Rechercher un chef..."
                value={chefSearch}
                onChange={(e) => setChefSearch(e.target.value)}
              />
            </div>

            <div className="border rounded-lg max-h-64 overflow-y-auto mb-4">
              {filteredChefs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Aucun chef trouve</p>
              ) : (
                filteredChefs.map((chef) => (
                  <div
                    key={chef.id}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <div>
                      <p className="font-medium">{chef.nomComplet}</p>
                      <p className="text-sm text-gray-500">{chef.email}</p>
                      {chef.equipeActuelle && (
                        <p className="text-xs text-orange-600">
                          Actuellement dans: {chef.equipeActuelle.nom}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        updateChefMutation.mutate({ equipeId: showChefModal, chefId: chef.id })
                      }
                      disabled={updateChefMutation.isPending}
                      className={`flex items-center px-3 py-1.5 rounded text-sm ${
                        chef.equipeActuelle
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                      }`}
                      title={chef.equipeActuelle ? 'Transferer' : 'Affecter'}
                    >
                      {chef.equipeActuelle ? (
                        <>
                          <ArrowsRightLeftIcon className="w-4 h-4 mr-1" />
                          Transferer
                        </>
                      ) : (
                        <>
                          <UserIcon className="w-4 h-4 mr-1" />
                          Affecter
                        </>
                      )}
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {nbLibres} libre(s)
                {' · '}
                {nbATransferer} a transferer
              </p>
              <button
                onClick={() => {
                  setShowChefModal(null);
                  setChefSearch('');
                }}
                className="btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

export default EquipesPage;
